import { Button, Card, Col, Collapse, Form, Modal, Row, Spin, Table } from "antd";
import { useEffect, useState } from "react";
import OrganizationCollapseView from "../../components/Organizations/OrganizationCollapseView";
import { DeleteOutlined, RightOutlined, UnderlineOutlined } from "@ant-design/icons";
import { HospiceOrganizationWithDetailsResponse } from "../../models/OrganizationModels";
import Title from "antd/lib/skeleton/Title";
import RoundedButton from "../../components/RoundedButton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../components/PageTitle";
import { useDeleteOrderSetMutation, useGetOrganizationOrderSetsQuery, usePostOrderSetsMutation, useUpdateOrderSetMutation } from "../../features/organization/organizationService";
import OrganizationHeader from "../../components/Organizations/OrganizationHeader";
import PlusButton from "../../components/PlusButton";
import OrderSetHeader from "../../components/OrderSets/OrganizationHeader";
import OrderSetsView from "../../components/OrderSets/OrderSetsView";
import { OrderSetRequest, OrderSetResponse, UpdateOrderSetRequest } from "../../models/OrderSetModels";
import { useDeleteOrderTemplateMutation } from "../../features/patient/patientService";
import { OrderTemplate } from "../../models/PatientModels";
import OrderSetForm from "../../components/OrderSets/OrderSetForm";
const { Panel } = Collapse;

const OrganizationOrderSets = () => {
    const { id } = useParams();
    const organizationId: string = id ?? "";
    const [form] = Form.useForm();

    const navigate = useNavigate();

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const organizationName = params.get("orgName") ?? "";
    const [openCreateModal, setCreateOpenModal] = useState<boolean>(false);
    const [openUpdateModal, setUpdateOpenModal] = useState<boolean>(false);
    const [selectedKit, setSelectedKit] = useState<OrderSetResponse>();

    const { data: orderSets, refetch, isLoading: isGetLoading } = useGetOrganizationOrderSetsQuery(organizationId);

    const [ createOrderSet, {isLoading: isPostLoading} ] = usePostOrderSetsMutation();
    const [ deleteOrderSet, {isLoading: isDeleteLoading} ] = useDeleteOrderSetMutation();
    const [ updateOrderSet, {isLoading: isUpdateLoading} ] = useUpdateOrderSetMutation();
    const [ deleteOrderTemplate, {isLoading: isOtDeleteLoading} ] = useDeleteOrderTemplateMutation();
    
    const handleCreateSubmit = async () => {
        const values = await form.validateFields();
        
        const req: OrderSetRequest = {
            name: values.name,
            description: values.description,
            organizationId: organizationId
        }
        
        await createOrderSet(req);
        closeForm();
        refetch();
    };

    const handleUpdateSubmit = async () => {
        const values = await form.validateFields();
        const data: OrderSetRequest = {
            name: values.name,
            description: values.description,
            organizationId: organizationId
        }

        const req: UpdateOrderSetRequest = {
            id: selectedKit!.id,
            data: data
        }
        
        setSelectedKit(undefined);
        await updateOrderSet(req);
        closeForm();
        refetch();
        
    };

    const handleDeleteSubmit = async (id: string) => {
        await deleteOrderSet(id);
        refetch();
    };

    const onCreate = () => {
        form.resetFields();
        setCreateOpenModal(true)
    }

    const onUpdate = (orderSet: OrderSetResponse) => {
        form.resetFields();

        setSelectedKit(orderSet);
        setUpdateOpenModal(true);
    }

    const handleOrderTemplateDelete = async (orderSetId: string, templateId: string) => {
        await deleteOrderTemplate(templateId);
        refetch();
    }

    const closeForm = () => {
        setCreateOpenModal(false);
        setUpdateOpenModal(false);
    }

    return (
        <Spin spinning={isGetLoading || isPostLoading || isOtDeleteLoading || isDeleteLoading || isUpdateLoading}>
            <h1 className="d-flex align-items-center justify-content-between">
                <PageTitle title={`Comfort Kits/Order Sets of ${organizationName}`} onClick={() => navigate(-1)} />
                <RoundedButton
                    className="mt-2 me-2 bg-light-blue text-white"
                    onClick={onCreate}
                >
                    Add New Comfort Kit
                </RoundedButton>
            </h1>
            {
               orderSets && orderSets.length > 0 ?
        
                <div className="p-5">
    
                    <Collapse className="bg-transparent" style={{minWidth: "1100px"}}>
                        {orderSets.map((orderSet) => (
                            <Panel
                                showArrow={true}
                                className="mb-2 bg-collapse-light-blue"
                                key={orderSet.id}
                                header={
                                    <div className="d-flex align-items-center justify-content-between">
                                        <OrderSetHeader
                                            headerText={`${orderSet.name}`}
                                        />
                                        <div className="d-flex align-items-center">
                                            <RoundedButton
                                                className="bg-medium-blue text-white me-4"
                                                onClick={(e) => { 
                                                    e.stopPropagation();
                                                    onUpdate(orderSet);
                                                }}
                                            >
                                                Manage Comfort Kit/Order Set
                                            </RoundedButton>

                                            <DeleteOutlined rev={undefined} className="delete-button fs-3 me-1" 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteSubmit(orderSet.id)
                                            }}
                                            />
                                        </div>
                                    </div>
                                }
                            >
                                <div>
                                    <div className="d-flex justify-content-end">
                                        <PlusButton
                                            className="mb-2 bg-white"
                                            plusBackgroundColor="var(--hcrx-light-green)"
                                            onClick={() => navigate(`${orderSet.id}/med-create?setName=${orderSet.name}`)}
                                        >
                                            Add New Medication
                                        </PlusButton>
                                    </div>

                                    <Row wrap className="border border-dark-subtle mb-2 d-flex align-items-stretch">
                                        <Col span={5} className="p-3 fs-7 bg-grey align-content-center">
                                            Description
                                        </Col>
                                        <Col span={19} className="p-3 fs-7 bg-white text-break">
                                            {orderSet.description}
                                        </Col>
                                    </Row>

                                    <OrderSetsView orderSet={orderSet} isAdminView={true} handleFunction={handleOrderTemplateDelete}/>
                                </div>
                            </Panel>
                        ))}
                    </Collapse>
                </div>
                :
                <div className="d-flex align-items-center justify-content-center fs-5 text-light-gray m-5">There are no Comfort Kits/Order Sets to be displayed.</div>
            }
            <OrderSetForm isUpdate={false} organizationName={organizationName} form={form} handleSubmit={handleCreateSubmit} openModal={openCreateModal} onCancel={closeForm}  />
            {selectedKit && <OrderSetForm isUpdate={true} kitName={selectedKit?.name} kitDesc={selectedKit?.description} form={form} handleSubmit={handleUpdateSubmit} openModal={openUpdateModal} onCancel={closeForm}  />}
        </Spin>
    )
}

export default OrganizationOrderSets;